// ============================================
// chartOptionFactory（A-03 统一渲染壳）
// 输入：组件类型 + 属性配置（config/base/special 扁平化）+ 数据行
// 输出：echarts option | null（未知类型返回 null，走插件/占位渲染）
// 约定：新组件 = schema 定义 + 本文件 option 分支 + 数据引导 + 注册，零 React 组件代码
// A-09：支持后端发布的声明式组件（optionTemplate 注册后由本工厂统一渲染）
// ============================================

import * as echarts from 'echarts';
import { buildDeclarativeOption as buildSharedDeclarativeOption, parseOptionAdvanced } from '@dtwin/shared';
import { normalizeRows, rowLabel, rowValue } from './utils';

/** 象形树柱图默认符号（path:// 松树剪影；可在属性面板用 symbolUrl 覆盖为图片） */
const TREE_SYMBOL_PATH = 'path://M16 0 L32 30 L23 30 L36 56 L26 56 L40 84 L2 84 L16 56 L6 56 L19 30 L7 30 Z';

/** 声明式组件模板（A-07/A-09）：后端发布的 option 模板 + 数据映射 */
export interface DeclarativeTemplate {
  option: Record<string, unknown>;
  /** 数据映射：'label/value'（单系列）或 'v1,v2,v3'（多系列 key） */
  dataMapping?: string;
  /** 2026-10-15（组件编辑器 P0）：schema.field → option 路径（结构化属性注入；支持 '*' 通配；禁 data 段） */
  fieldPaths?: Record<string, string> | null;
}

const declarativeTemplates = new Map<string, DeclarativeTemplate>();

/** 注册声明式组件模板（plugin-manager 拉取市场后调用） */
export function registerDeclarativeTemplate(type: string, tpl: DeclarativeTemplate): void {
  declarativeTemplates.set(type, tpl);
}

export function unregisterDeclarativeTemplate(type: string): void {
  declarativeTemplates.delete(type);
}

export function getDeclarativeTemplate(type: string): DeclarativeTemplate | undefined {
  return declarativeTemplates.get(type);
}

/** 声明式模板 → echarts option（克隆模板 + fieldPaths + 高级选项合并 + 按 dataMapping 注入数据）
 *  2026-10-15（组件编辑器 P0）：渲染管线迁至 @dtwin/shared —— 编辑器实时预览/服务端 SSR/运行时单一实现 */
function buildDeclarativeOption(tpl: DeclarativeTemplate, rows: Array<any>, config?: Record<string, unknown> | null): any {
  if (config?.optionAdvanced && !parseOptionAdvanced(config.optionAdvanced)) {
    console.warn('[chartOptionFactory] 高级选项（optionAdvanced）非法或超限，已忽略');
  }
  return buildSharedDeclarativeOption(tpl, { config, rows });
}

export function chartOptionFactory(
  type: string,
  config: Record<string, unknown>,
  data: unknown,
): any | null {
  // 2026-09-14 修复（FE-33/35）：数据归一化（对象/标量） + 标签/数值兜底（原 `Number(r.value) ?? 0` 中 Number() 永不 nullish，NaN 静默不绘制）
  const rows = normalizeRows(data) ?? [];
  // A-09：声明式组件优先（后端发布模板注册后统一走本工厂）
  const declarative = declarativeTemplates.get(type);
  if (declarative) return buildDeclarativeOption(declarative, rows, config);
  const labels = rows.map((r) => rowLabel(r));
  const values = rows.map((r) => rowValue(r));
  const hasData = rows.length > 0;
  const legend = config.legendShow === false ? undefined : {
    show: true,
    top: config.legendPosition === 'top' || !config.legendPosition ? 4 : undefined,
    right: config.legendPosition === 'right' ? 4 : undefined,
    bottom: config.legendPosition === 'bottom' ? 4 : undefined,
    left: config.legendPosition === 'left' ? 4 : undefined,
    orient: config.legendOrient || 'horizontal',
    textStyle: { color: '#ccc', fontSize: 11 },
  };

  switch (type) {
    case 'line-chart':
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月', '七月'], boundaryGap: false, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'line', smooth: config.smooth !== false, data: hasData ? values : [150, 230, 224, 218, 135, 147, 260], symbol: config.showSymbol === false ? 'none' : 'circle', lineStyle: { width: config.lineWidth || 3, color: config.lineColor || '#1890ff' }, areaStyle: config.showArea === false ? undefined : { opacity: config.areaOpacity ?? 1 } }],
      };
    case 'bar-chart':
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 40, right: 20, top: legend ? 36 : 20, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月'], axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'bar', data: hasData ? values : [120, 200, 150, 80, 70, 110], itemStyle: { color: config.barColor || '#1890ff', borderRadius: [config.barBorderRadius || 4, config.barBorderRadius || 4, 0, 0] } }],
      };
    case 'pie-chart': {
      const fallback = [
        { name: '搜索引擎', value: 1048 }, { name: '社交媒体', value: 735 },
        { name: '直接访问', value: 580 }, { name: '邮件营销', value: 484 },
      ];
      const d = hasData ? rows.map((r, i) => ({ name: rowLabel(r), value: rowValue(r), itemStyle: { color: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f'][i % 4] } })) : fallback;
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend,
        series: [{ type: 'pie', radius: config.isRing === false ? '70%' : ['40%', '70%'], center: ['50%', '50%'], roseType: config.roseType === 'none' ? undefined : config.roseType, label: { color: '#ccc' }, data: d }],
      };
    }
    case 'gauge':
      return {
        series: [{ type: 'gauge', min: config.gaugeMin || 0, max: config.gaugeMax || 100, detail: { formatter: `{value}${config.detailSuffix || '%'}` }, data: [{ value: rows[0]?.value ?? 65 }] }],
      };
    case 'radar':
      return {
        radar: { indicator: [{ name: 'A', max: 100 }, { name: 'B', max: 100 }, { name: 'C', max: 100 }, { name: 'D', max: 100 }, { name: 'E', max: 100 }] },
        series: [{ type: 'radar', data: [{ name: '数据', value: [85, 70, 90, 65, 80] }] }],
      };
    case 'scatter':
      return {
        grid: { left: 50, right: 20, top: legend ? 40 : 20, bottom: 40 },
        xAxis: { type: 'value' }, yAxis: { type: 'value' },
        series: [{ type: 'scatter', symbolSize: config.symbolSize || 10, data: rows.map((r) => r.value || [r.x, r.y]) }],
      };
    case 'ranking':
      return {
        grid: { left: 50, right: 60, top: 10, bottom: 10 },
        xAxis: { type: 'value', show: false },
        yAxis: { type: 'category', data: (hasData ? rows.map((r) => rowLabel(r)).reverse() : ['设备E', '设备A', '设备C']).slice(0, Number(config.topN || 8)) },
        series: [{ type: 'bar', data: (hasData ? values : [98, 92, 85]).slice(0, Number(config.topN || 8)), label: { show: true, position: 'right' } }],
      };
    case 'heatmap':
      return {
        grid: { left: 40, right: 20, top: 20, bottom: 30 },
        xAxis: { type: 'category', data: Array.from({ length: 24 }, (_, i) => `${i}h`) },
        yAxis: { type: 'category', data: ['周一', '周二', '周三'] },
        visualMap: { min: 0, max: 100, inRange: { color: ['#1a1a2e', '#1890ff', '#ff4d4f'] } },
        series: [{ type: 'heatmap', data: rows }],
      };
    // ---- A-04 批次1：柱/折/饼 变体 ----
    case 'pictogram-bar': {
      const symbol = String(config.symbol || 'rect');
      const demoValues = [120, 200, 150, 80, 70, 110];
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月'], axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'bar', data: hasData ? values : demoValues, barWidth: '40%', itemStyle: { color: config.barColor || '#1890ff' }, symbol, symbolRepeat: true, symbolSize: [26, 14] }],
      };
    }
    case 'stacked-bar': {
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3');
      const colors = splitList(config.stackColors, ['#1890ff', '#52c41a', '#faad14']);
      const demo = [[120, 132, 101, 134, 90, 230, 210], [220, 182, 191, 234, 290, 330, 310], [150, 232, 201, 154, 190, 330, 410]];
      const demoLabels = ['一月', '二月', '三月', '四月', '五月', '六月', '七月'];
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 40, right: 20, top: legend ? 36 : 20, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : demoLabels, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: keys.map((k, i) => ({
          type: 'bar',
          name: k,
          stack: 'total',
          data: hasData ? rows.map((r) => Number(r[k]) || 0) : (demo[i % demo.length] as number[]),
          itemStyle: { color: colors[i % colors.length] },
        })),
      };
    }
    case 'hbar': {
      const d = labels.length ? labels.slice().reverse() : ['任务A', '任务B', '任务C'];
      const v = values.length ? values.slice().reverse() : [85, 62, 45];
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 60, right: 60, top: legend ? 36 : 16, bottom: 30 },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'category', data: d, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        series: [{ type: 'bar', data: v, itemStyle: { color: config.barColor || '#1890ff', borderRadius: [0, 4, 4, 0] }, label: config.showValue === false ? undefined : { show: true, position: 'right', color: '#ccc' } }],
      };
    }
    case 'area-line': {
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月', '七月'], boundaryGap: false, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'line', smooth: true, data: hasData ? values : [150, 230, 224, 218, 135, 147, 260], lineStyle: { width: 3, color: config.lineColor || '#1890ff' }, areaStyle: { opacity: config.areaOpacity ?? 0.6 }, symbol: 'circle', symbolSize: 8 }],
      };
    }
    case 'multi-line': {
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3');
      const colors = splitList(config.lineColors, ['#1890ff', '#52c41a', '#faad14']);
      const demo = [[120, 132, 101, 134, 90, 230, 210], [220, 182, 191, 234, 290, 330, 310], [150, 232, 201, 154, 190, 330, 410]];
      const demoLabels = ['一月', '二月', '三月', '四月', '五月', '六月', '七月'];
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : demoLabels, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: keys.map((k, i) => ({
          type: 'line',
          name: k,
          smooth: config.smooth !== false,
          data: hasData ? rows.map((r) => Number(r[k]) || 0) : (demo[i % demo.length] as number[]),
          lineStyle: { width: Number(config.lineWidth) || 3, color: colors[i % colors.length] },
          symbol: config.showSymbol === false || String(config.symbol) === 'none' ? 'none' : String(config.symbol || 'circle'),
          symbolSize: 7,
          connectNulls: config.connectNulls === true,
        })),
      };
    }
    case 'step-line': {
      // 2026-10-13 修复（用户反馈"数据点样式/阶梯无效"）：此前硬编码 step='middle'/symbol='circle' → 读取 subgroup.line 配置
      const stepMode = String(config.step || 'middle');
      const symbolStyle = String(config.symbol || 'circle');
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月', '七月'], axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{
          type: 'line',
          step: stepMode === 'none' ? undefined : stepMode,
          smooth: config.smooth === true,
          data: hasData ? values : [150, 230, 224, 218, 135, 147, 260],
          lineStyle: { width: Number(config.lineWidth) || 3, color: config.lineColor || '#1890ff' },
          symbol: config.showSymbol === false || symbolStyle === 'none' ? 'none' : symbolStyle,
          symbolSize: config.showSymbol === false || symbolStyle === 'none' ? 0 : 8,
          connectNulls: config.connectNulls === true,
        }],
      };
    }
    case 'doughnut': {
      const d = hasData ? rows.map((r, i) => ({ name: rowLabel(r), value: rowValue(r), itemStyle: { color: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f'][i % 4] } })) : [
        { name: '甲', value: 30 }, { name: '乙', value: 40 }, { name: '丙', value: 30 },
      ];
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend,
        series: [{ type: 'pie', radius: [`${config.innerRadius || 40}%`, `${config.outerRadius || 70}%`], center: ['50%', '50%'], label: { color: '#ccc' }, data: d }],
      };
    }
    case 'rose': {
      const d = hasData ? rows.map((r, i) => ({ name: rowLabel(r), value: rowValue(r), itemStyle: { color: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1'][i % 5] } })) : [
        { name: '玫瑰A', value: 40 }, { name: '玫瑰B', value: 30 }, { name: '玫瑰C', value: 20 },
      ];
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend,
        series: [{ type: 'pie', radius: ['10%', '70%'], center: ['50%', '50%'], roseType: config.roseMode || 'radius', label: { color: '#ccc' }, data: d }],
      };
    }
    case 'multi-ring': {
      const keys = splitKeys(config.seriesKeys, 'v1,v2');
      const colors = splitList(config.ringColors, ['#1890ff', '#52c41a']);
      const d0 = rows.map((r, i) => ({ name: rowLabel(r), value: Number(r[keys[0]]) || Number(r.value) || 0, itemStyle: { color: colors[0] } }));
      const d1 = rows.map((r, i) => ({ name: rowLabel(r), value: Number(r[keys[1]]) || 0, itemStyle: { color: colors[1 % colors.length] } }));
      const fallback = [{ name: 'A', value: 30 }, { name: 'B', value: 40 }];
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        legend,
        series: [
          { type: 'pie', radius: ['0%', '40%'], center: ['50%', '50%'], label: { color: '#ccc' }, data: d0.length ? d0 : fallback },
          { type: 'pie', radius: ['45%', '70%'], center: ['50%', '50%'], label: { color: '#ccc' }, data: d1.length ? d1 : fallback },
        ],
      };
    }
    // ---- A-05 批次2：散点/地图/仪表 扩展 ----
    case 'scatter-bubble': {
      const sizeKey = String(config.sizeKey || 'size');
      return {
        tooltip: { trigger: 'item' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 20, bottom: 40 },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } } },
        series: [{ type: 'scatter', symbolSize: (d: any) => Math.max(6, Number(d[sizeKey] ?? d.value ?? 10)), itemStyle: { color: config.color || '#1890ff', opacity: 0.75 }, data: rows }],
      };
    }
    case 'scatter-regression': {
      const pts: [number, number][] = rows.map((r) => [Number(r.x) || 0, Number(r.y) || 0]);
      const fit = linearFit(pts);
      return {
        tooltip: { trigger: 'item' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 20, bottom: 40 },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } } },
        series: [
          { name: '散点', type: 'scatter', symbolSize: 10, itemStyle: { color: '#1890ff' }, data: pts },
          ...(fit ? [{ name: '回归线', type: 'line', smooth: true, symbol: 'none', lineStyle: { width: 2, color: config.lineColor || '#faad14' }, data: fit }] : []),
        ],
      };
    }
    case 'map-province': {
      const adcode = String(config.adcode || '440000');
      const geoUrl = `/assets/geo/province-${adcode}.json`;
      const geoName = `province-${adcode}`;
      return {
        __geoUrl: geoUrl,
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        legend,
        series: [{ type: 'map', map: geoName, roam: true, label: { show: true, color: '#fff', fontSize: 10 }, itemStyle: { borderColor: '#3a5a80', areaColor: '#12304f' }, emphasis: { label: { color: '#fff' } }, data: rows.map((r) => ({ name: r.name, value: Number(r.value) || 0 })) }],
      };
    }
    case 'map-city': {
      const adcode = String(config.adcode || '440100');
      const geoUrl = `/assets/geo/city-${adcode}.json`;
      const geoName = `city-${adcode}`;
      return {
        __geoUrl: geoUrl,
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        legend,
        series: [{ type: 'map', map: geoName, roam: true, label: { show: true, color: '#fff', fontSize: 10 }, itemStyle: { borderColor: '#3a5a80', areaColor: '#12304f' }, emphasis: { label: { color: '#fff' } }, data: rows.map((r) => ({ name: r.name, value: Number(r.value) || 0 })) }],
      };
    }
    case 'map-migration': {
      const fromKey = String(config.fromKey || 'from');
      const toKey = String(config.toKey || 'to');
      const valueKey = String(config.valueKey || 'value');
      const geoUrl = '/assets/geo/china.json';
      const lines = rows
        .map((r) => ({ from: String(r[fromKey] || ''), to: String(r[toKey] || ''), value: Number(r[valueKey]) || 1 }))
        .filter((l) => l.from && l.to);
      return {
        __geoUrl: geoUrl,
        __geoCoord: true,
        tooltip: { trigger: 'item' },
        legend,
        geo: { map: 'china-map', roam: true, itemStyle: { borderColor: '#3a5a80', areaColor: '#12304f' }, label: { show: false } },
        series: [
          { type: 'lines', coordinateSystem: 'geo', effect: { show: true, period: 4, trailLength: 0.2, symbol: 'arrow', symbolSize: 6 }, lineStyle: { color: '#ffd666', width: 1, opacity: 0.7, curveness: 0.25 }, data: lines.map((l) => ({ coords: [[0, 0], [0, 0]], name: `${l.from} → ${l.to}`, value: l.value, _fromName: l.from, _toName: l.to })) },
          { type: 'scatter', coordinateSystem: 'geo', symbolSize: 8, itemStyle: { color: '#52c41a' }, data: [] },
        ],
      };
    }
    case 'gauge-multi': {
      const d = hasData ? rows.map((r) => ({ name: rowLabel(r), value: rowValue(r) })) : [{ name: '表盘A', value: 65 }, { name: '表盘B', value: 42 }];
      return {
        series: d.map((g) => ({
          type: 'gauge', min: config.gaugeMin || 0, max: config.gaugeMax || 100,
          radius: '70%', center: ['50%', `${d.indexOf(g) === 0 ? 25 : 75}%`],
          title: { show: true, color: '#ccc', fontSize: 12 }, detail: { formatter: `{value}${config.detailSuffix || '%'}`, color: '#1890ff', fontSize: 20 },
          data: [{ value: g.value, name: g.name }],
        })),
      };
    }
    case 'funnel-compare': {
      const keys = splitKeys(config.seriesKeys, 'v1,v2');
      const colors = ['#1890ff', '#faad14'];
      return {
        tooltip: { trigger: 'item', formatter: '{a}: {c} ({d}%)' },
        legend,
        series: keys.map((k, i) => ({
          name: k, type: 'funnel', left: i === 0 ? '5%' : '55%', top: 20, width: '38%', minSize: '20%',
          label: { show: true, position: 'inside', color: '#fff' }, itemStyle: { color: colors[i % 2] },
          data: rows.map((r) => ({ name: rowLabel(r), value: Number(r[k]) || 0 })),
        })),
      };
    }
    case 'bar-3d': {
      const xKey = String(config.xKey || 'x');
      const zKey = String(config.zKey || 'z');
      const d = rows.map((r) => ({ name: String(r[xKey] ?? r.label ?? ''), value: Number(r[zKey] ?? r.value) || 0 }));
      const fallback = [{ name: 'A', value: 80 }, { name: 'B', value: 120 }, { name: 'C', value: 60 }];
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 40, right: 20, top: legend ? 36 : 16, bottom: 40 },
        xAxis: { type: 'category', data: d.length ? d.map((x) => x.name) : fallback.map((x) => x.name), axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'bar', data: d.length ? d.map((x) => x.value) : fallback.map((x) => x.value), barWidth: '45%', itemStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: config.barColor || '#1890ff' }, { offset: 1, color: 'rgba(24,144,255,0.35)' }] }, borderRadius: [6, 6, 0, 0], shadowColor: 'rgba(24,144,255,0.35)', shadowBlur: 12 } }],
      };
    }
    // ---- A-06 批次3：扩展图库 ----
    case 'liquid': {
      const v = hasData ? (Number(rows[0].value) || 0) : 0.6;
      return {
        __ext: ['liquidfill'],
        series: [{ type: 'liquidFill', data: [v], radius: '80%', color: [config.color || '#1890ff'], shape: config.shape || 'circle', label: { fontSize: 26, color: '#fff' }, outline: { show: true, borderDistance: 4, itemStyle: { borderColor: config.color || '#1890ff', borderWidth: 2 } } }],
      };
    }
    case 'sunburst': {
      const radius = String(config.radius || '0%,80%').split(',').map((s) => s.trim());
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend,
        series: [{ type: 'sunburst', radius: radius.length === 2 ? radius : ['0%', '80%'], data: rows.length ? rows : [{ name: '根', children: [{ name: '叶子A', value: 30 }, { name: '叶子B', value: 20 }] }], label: { color: '#ccc' } }],
      };
    }
    case 'tree': {
      const root = rows.length && rows[0].children ? rows[0] : { name: '根节点', children: rows.length ? rows : [{ name: '子A', value: 1 }, { name: '子B', value: 2 }] };
      return {
        tooltip: { trigger: 'item' },
        series: [{ type: 'tree', orient: config.orient || 'TB', rootLocation: { x: '50%', y: config.orient === 'LR' ? '50%' : '10%' }, symbol: 'circle', symbolSize: 8, lineStyle: { color: '#3a5a80' }, label: { color: '#ccc', position: config.orient === 'LR' ? 'right' : 'top' }, leaves: { label: { color: '#aaa', position: config.orient === 'LR' ? 'right' : 'bottom' } }, data: [root] }],
      };
    }
    case 'wordcloud': {
      const [min, max] = String(config.sizeRange || '12,60').split(',').map((s) => Number(s) || 0);
      return {
        __ext: ['wordcloud'],
        tooltip: { trigger: 'item' },
        series: [{ type: 'wordCloud', shape: 'circle', sizeRange: [min || 12, max || 60], rotationRange: [-45, 45], gridSize: 6, textStyle: { color: () => ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1'][Math.floor(Math.random() * 5)] }, data: hasData ? rows.map((r) => ({ name: r.name ?? r.label, value: Number(r.value) || 1 })) : [{ name: 'DTwince', value: 100 }, { name: '数字孪生', value: 80 }, { name: '可视化', value: 60 }] }],
      };
    }
    case 'sankey-enhanced': {
      const nodes = Array.from(new Set(rows.flatMap((r) => [r.from, r.to]))).filter(Boolean).map((n) => ({ name: n }));
      return {
        tooltip: { trigger: 'item', triggerOn: 'mousemove' },
        legend,
        series: [{ type: 'sankey', nodeAlign: config.nodeAlign || 'justify', data: nodes, links: rows.map((r) => ({ source: r.from, target: r.to, value: Number(r.value) || 1 })), lineStyle: { color: 'gradient', opacity: 0.4 } }],
      };
    }
    case 'graph': {
      const nodes = Array.from(new Set(rows.flatMap((r) => [r.name ?? r.from, r.to]))).filter(Boolean).map((n, i) => ({ name: n, symbolSize: 24, itemStyle: { color: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f'][i % 4] } }));
      const links = rows.filter((r) => r.to).map((r) => ({ source: r.name ?? r.from, target: r.to, value: Number(r.value) || 1 }));
      return {
        tooltip: { trigger: 'item' },
        legend,
        series: [{ type: 'graph', layout: config.layout || 'force', roam: true, data: nodes, links, force: { repulsion: 120, edgeLength: [60, 120] }, lineStyle: { color: 'source', opacity: 0.6, width: 1 }, label: { color: '#ccc' } }],
      };
    }
    case 'heatmap-calendar': {
      const year = Number(config.year) || 2026;
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        visualMap: { min: 0, max: 100, calculable: true, orient: 'horizontal', left: 'center', top: 6, inRange: { color: ['#1a1a2e', '#1890ff', '#ff4d4f'] } },
        calendar: { range: year, cellSize: ['auto', 16], itemStyle: { borderWidth: 2, borderColor: '#0f0f1a' }, yearLabel: { color: '#aaa' }, dayLabel: { color: '#aaa' }, monthLabel: { color: '#aaa' } },
        series: [{ type: 'heatmap', coordinateSystem: 'calendar', data: rows.map((r) => [String(r.date ?? rowLabel(r)), Number(r.value) || 0]) }],
      };
    }
    case 'radar-advanced': {
      const indicators = String(config.indicators || 'A,B,C,D,E').split(',').map((s) => s.trim()).filter(Boolean).map((n, i) => ({ name: n, max: 100 }));
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3');
      const colors = ['#1890ff', '#52c41a', '#faad14'];
      return {
        tooltip: { trigger: 'item' },
        legend,
        radar: { indicator: indicators.length ? indicators : [{ name: 'A', max: 100 }, { name: 'B', max: 100 }, { name: 'C', max: 100 }], axisName: { color: '#ccc' }, splitLine: { lineStyle: { color: '#2a2a3e' } } },
        series: [{ type: 'radar', data: keys.map((k, i) => ({ name: k, value: rows.map((r) => Number(r[k]) || 0), areaStyle: { color: colors[i % 3], opacity: 0.25 } })) }],
      };
    }
    // ---- G-00A 批次A：简单变体（13 个）----
    case 'smooth-line': {
      const markValue = Number(config.markLineValue) || 0;
      const useStep = config.step && config.step !== 'none' ? String(config.step) : undefined;
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月', '七月'], boundaryGap: false, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined } },
        series: [{
          type: 'line',
          smooth: config.smooth !== false,
          step: useStep,
          connectNulls: config.connectNulls === true,
          data: hasData ? values : [150, 230, 224, 218, 135, 147, 260],
          symbol: config.showSymbol === false ? 'none' : String(config.symbol || 'circle'),
          symbolSize: 8,
          lineStyle: { width: Number(config.lineWidth) || 3, color: config.lineColor || '#1890ff' },
          markLine: markValue ? { silent: true, symbol: 'none', data: [{ yAxis: markValue }], lineStyle: { color: '#faad14', type: 'dashed' }, label: { color: '#faad14' } } : undefined,
        }],
      };
    }
    case 'stacked-area': {
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3');
      const colors = splitList(config.stackColors, ['#1890ff', '#52c41a', '#faad14']);
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月'], boundaryGap: false, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: keys.map((k, i) => ({
          type: 'line', name: k, stack: 'total', smooth: config.smooth !== false,
          data: rows.map((r) => Number(r[k]) || 0),
          symbol: String(config.symbol || 'circle'),
          lineStyle: { width: Number(config.lineWidth) || 2, color: colors[i % colors.length] },
          areaStyle: { opacity: Number(config.areaOpacity ?? 0.6) },
        })),
      };
    }
    case 'gradient-area': {
      const stops = parseJson<string[]>(config.gradientColors, ['#1890ff', 'rgba(24,144,255,0.05)']);
      const c0 = stops[0] || '#1890ff';
      const c1 = stops[1] || 'rgba(24,144,255,0.05)';
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月', '七月'], boundaryGap: false, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{
          type: 'line', smooth: config.smooth !== false,
          data: hasData ? values : [150, 230, 224, 218, 135, 147, 260],
          symbol: config.symbol === 'none' ? 'none' : String(config.symbol || 'circle'), symbolSize: 7,
          lineStyle: { width: Number(config.lineWidth) || 3, color: c0 },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: c0 }, { offset: 1, color: c1 }] }, opacity: Number(config.areaOpacity ?? 1) },
        }],
      };
    }
    case 'radar-fill': {
      const indicators = parseJson<Array<{ name: string; max: number }>>(config.indicatorJson, []);
      const ind = indicators.length ? indicators : [{ name: 'A', max: 100 }, { name: 'B', max: 100 }, { name: 'C', max: 100 }, { name: 'D', max: 100 }, { name: 'E', max: 100 }];
      return {
        tooltip: { trigger: 'item' },
        radar: { indicator: ind, splitArea: { show: config.splitArea !== false }, splitLine: { lineStyle: { color: '#2a2a3e' } }, axisName: { color: '#ccc' } },
        series: [{
          type: 'radar',
          data: [{ name: '数据', value: ind.map((_, i) => Number(rows[i]?.value) || [85, 70, 90, 65, 80][i % 5]), areaStyle: { opacity: Number(config.areaOpacity ?? 0.35) } }],
          symbolSize: Number(config.symbolSize) || 6,
          lineStyle: { width: Number(config.lineWidth) || 2 },
        }],
      };
    }
    case 'nightingale': {
      const palette = parseJson<string[]>(config.colorPalette, []);
      const colors = palette.length ? palette : ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1'];
      const d = hasData ? rows.map((r, i) => ({ name: rowLabel(r), value: rowValue(r), itemStyle: { color: colors[i % colors.length] } })) : [
        { name: '甲', value: 40 }, { name: '乙', value: 30 }, { name: '丙', value: 20 }, { name: '丁', value: 10 },
      ];
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend,
        series: [{ type: 'pie', roseType: config.roseMode === 'radius' ? 'radius' : 'area', radius: ['20%', '72%'], center: ['50%', '52%'], label: { show: config.showLabel !== false, position: config.labelPosition === 'inside' ? 'inside' : 'outside', color: '#ccc' }, data: d }],
      };
    }
    case 'pyramid': {
      const d = hasData ? rows.map((r) => ({ name: rowLabel(r), value: rowValue(r) })) : [
        { name: '层级A', value: 100 }, { name: '层级B', value: 80 }, { name: '层级C', value: 60 }, { name: '层级D', value: 40 },
      ];
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        legend,
        series: [{ type: 'funnel', sort: 'ascending', gap: Number(config.gap ?? 4), left: '10%', width: String(config.pyramidWidth || '80%'), top: 20, bottom: 20, minSize: '0%', label: { show: true, position: config.labelPosition === 'outside' ? 'right' : 'inside', color: config.labelPosition === 'outside' ? '#ccc' : '#fff' }, data: d.slice().sort((a, b) => a.value - b.value) }],
      };
    }
    case 'liquid-gradient': {
      const v = hasData ? (Number(rows[0].value) || 0) : 0.6;
      const colors = parseJson<string[]>(config.gradientColors, []);
      const base = String(config.color || '#1890ff');
      return {
        __ext: ['liquidfill'],
        series: [{ type: 'liquidFill', data: [v], radius: '80%', color: colors.length ? colors : [base, 'rgba(24,144,255,0.45)'], shape: String(config.shape || 'circle'), label: { fontSize: 26, color: '#fff' }, outline: { show: true, borderDistance: 4, itemStyle: { borderColor: base, borderWidth: 2 } } }],
      };
    }
    case 'liquid-multi': {
      const levels = parseJson<number[]>(config.levels, []);
      const data = levels.length ? levels : [0.7, 0.45, 0.2];
      return {
        __ext: ['liquidfill'],
        series: [{ type: 'liquidFill', data, radius: '80%', color: [String(config.color || '#1890ff')], shape: String(config.shape || 'circle'), label: { fontSize: 24, color: '#fff' }, outline: { show: true, borderDistance: 4, itemStyle: { borderColor: String(config.color || '#1890ff'), borderWidth: 2 } } }],
      };
    }
    case 'hbar-stacked': {
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3');
      const colors = splitList(config.stackColors, ['#1890ff', '#52c41a', '#faad14']);
      const d = hasData ? labels.slice().reverse() : ['任务A', '任务B', '任务C'];
      const rowsRev = hasData ? rows.slice().reverse() : [{ v1: 30, v2: 20 }, { v1: 40, v2: 25 }, { v1: 25, v2: 30 }];
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 60, right: 40, top: legend ? 36 : 16, bottom: 30 },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'category', data: d, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        series: keys.map((k, i) => ({ type: 'bar', name: k, stack: 'total', data: rowsRev.map((r) => Number(r[k]) || 0), barWidth: String(config.barWidth || '30'), itemStyle: { color: colors[i % colors.length] } })),
      };
    }
    case 'hbar-rank': {
      const topN = Number(config.topN) || 8;
      const sorted = (hasData ? rows.slice() : [{ label: '设备A', value: 95 }, { label: '设备B', value: 88 }, { label: '设备C', value: 76 }])
        .sort((a, b) => Number(b.value) - Number(a.value))
        .slice(0, topN);
      const d = sorted.map((r) => rowLabel(r)).reverse();
      const v = sorted.map((r) => rowValue(r)).reverse();
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 70, right: 60, top: 16, bottom: 30 },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'category', data: d, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        series: [{ type: 'bar', data: v.map((x, i) => ({ value: x, itemStyle: { color: i === v.length - 1 ? '#faad14' : String(config.barColor || '#1890ff'), borderRadius: [0, 4, 4, 0] } })), label: config.showValue !== false ? { show: true, position: 'right', color: '#ccc' } : undefined }],
      };
    }
    case 'heatmap-large': {
      const gridRows = Math.max(1, Number(config.gridRows) || 7);
      const cols = 24;
      const yCats = Array.from({ length: gridRows }, (_, i) => `行${i + 1}`);
      const colors = parseJson<string[]>(config.visualMapColors, []);
      const matrix: Array<[number, number, number]> = rows.length
        ? rows.map((r, i) => [i % cols, Math.floor(i / cols) % gridRows, Number(r.value) || 0] as [number, number, number])
        : Array.from({ length: cols * gridRows }, (_, i) => [i % cols, Math.floor(i / cols) % gridRows, Math.round(Math.abs(Math.sin(i)) * 100)] as [number, number, number]);
      return {
        tooltip: { position: 'top' },
        grid: { left: 50, right: 20, top: 20, bottom: 50 },
        xAxis: { type: 'category', data: Array.from({ length: cols }, (_, i) => `${i}h`), axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined }, splitArea: { show: true } },
        yAxis: { type: 'category', data: yCats, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined }, splitArea: { show: true } },
        visualMap: { min: Number(config.visualMapMin ?? 0), max: Number(config.visualMapMax ?? 100), calculable: true, orient: 'horizontal', left: 'center', bottom: 6, inRange: { color: colors.length ? colors : ['#1a1a2e', '#1890ff', '#ff4d4f'] } },
        series: [{ type: 'heatmap', data: matrix, label: { show: config.labelShow === true, color: '#fff', fontSize: 10 } }],
      };
    }
    case 'progress-gauge': {
      const value = hasData ? Number(rows[0]?.value) || 0 : 65;
      return {
        series: [{
          type: 'gauge',
          min: Number(config.gaugeMin ?? 0), max: Number(config.gaugeMax ?? 100),
          startAngle: Number(config.startAngle ?? 220), endAngle: Number(config.endAngle ?? -40),
          radius: '85%',
          progress: { show: config.showProgress !== false, width: Number(config.axisWidth) || 10, roundCap: true, itemStyle: { color: String(config.progressColor || '#52c41a') } },
          axisLine: { lineStyle: { width: Number(config.axisWidth) || 10, color: [[1, 'rgba(255,255,255,0.08)']] } },
          axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false }, pointer: { show: false },
          detail: { valueAnimation: true, formatter: `{value}${config.detailSuffix || '%'}`, color: '#fff', fontSize: 24, offsetCenter: [0, '10%'] },
          data: [{ value }],
        }],
      };
    }
    case 'calendar': {
      const year = Number(config.year) || 2026;
      const colors = parseJson<string[]>(config.visualMapColors, []);
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        visualMap: { min: Number(config.visualMapMin ?? 0), max: Number(config.visualMapMax ?? 100), calculable: true, orient: 'horizontal', left: 'center', top: 6, inRange: { color: colors.length ? colors : ['#1a1a2e', '#1890ff', '#ff4d4f'] } },
        calendar: { range: year, cellSize: ['auto', 16], itemStyle: { borderWidth: 2, borderColor: '#0f0f1a' }, yearLabel: { color: '#aaa' }, dayLabel: { color: '#aaa' }, monthLabel: { color: '#aaa' } },
        series: [{ type: 'heatmap', coordinateSystem: 'calendar', data: rows.map((r) => [String(r.date ?? rowLabel(r)), Number(r.value) || 0]) }],
      };
    }
    // ---- G-00B 批次B：图表扩展（14 个）----
    case 'waterfall': {
      const vals = hasData ? values : [120, 60, -40, 80, -20, 50];
      const cats = hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月'];
      let running = 0;
      const base: number[] = [];
      const rise: number[] = [];
      const fall: number[] = [];
      vals.forEach((v) => {
        base.push(v >= 0 ? running : running + v);
        rise.push(v >= 0 ? v : 0);
        fall.push(v < 0 ? -v : 0);
        running += v;
      });
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: cats, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [
          { type: 'bar', stack: 'wf', silent: true, itemStyle: { color: 'transparent' }, data: base },
          { type: 'bar', stack: 'wf', name: '上升', data: rise, itemStyle: { color: config.upColor || '#52c41a' }, label: { show: true, position: 'top', color: '#ccc', fontSize: 10 } },
          { type: 'bar', stack: 'wf', name: '下降', data: fall, itemStyle: { color: config.downColor || '#ff4d4f' }, label: { show: true, position: 'bottom', color: '#ccc', fontSize: 10 } },
        ],
      };
    }
    case 'polar-bar': {
      const inner = Number(config.innerRadius) || 10;
      return {
        tooltip: { trigger: 'axis' },
        legend,
        polar: { radius: [`${inner}%`, '75%'] },
        angleAxis: { type: 'category', data: hasData ? labels : ['A', 'B', 'C', 'D', 'E'], startAngle: Number(config.startAngle) || 90, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        radiusAxis: { splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'bar', coordinateSystem: 'polar', data: hasData ? values : [40, 60, 80, 55, 70], itemStyle: { color: config.barColor || '#1890ff', borderRadius: [4, 4, 0, 0] } }],
      };
    }
    case 'dynamic-bar': {
      const sorted = (hasData ? rows.map((r) => ({ label: rowLabel(r), value: Number(r.value) || 0 })) : [{ label: 'A', value: 80 }, { label: 'B', value: 60 }, { label: 'C', value: 40 }])
        .sort((a, b) => a.value - b.value);
      return {
        grid: { left: 70, right: 50, top: 12, bottom: 24 },
        xAxis: { type: 'value', max: 'dataMax', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'category', data: sorted.map((s) => s.label), animationDuration: 300, animationDurationUpdate: 300, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'bar', realtimeSort: true, data: sorted.map((s) => s.value), itemStyle: { color: config.barColor || '#1890ff', borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', color: '#ccc' } }],
      };
    }
    case 'realtime-line': {
      const win = Math.max(5, Number(config.windowSize) || 30);
      const src = hasData ? values : Array.from({ length: win }, (_, i) => 50 + Math.round(Math.sin(i / 3) * 20));
      const data = src.slice(-win);
      return {
        grid: { left: 50, right: 20, top: 20, bottom: 30 },
        xAxis: { type: 'category', data: data.map((_, i) => String(i + 1)), boundaryGap: false, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'line', smooth: true, showSymbol: false, data, lineStyle: { width: Number(config.lineWidth) || 2, color: config.lineColor || '#1890ff' }, areaStyle: { opacity: 0.15 } }],
      };
    }
    case 'mark-line': {
      const threshold = Number(config.threshold) || 200;
      const color = String(config.thresholdColor || '#faad14');
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月', '七月'], boundaryGap: false, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{
          type: 'line', smooth: true, data: hasData ? values : [150, 230, 224, 218, 135, 147, 260],
          symbol: 'circle', symbolSize: 7,
          lineStyle: { width: Number(config.lineWidth) || 3, color: config.lineColor || '#1890ff' },
          markLine: { silent: true, symbol: 'none', data: [{ yAxis: threshold }], lineStyle: { color, type: 'dashed' }, label: { color, formatter: `阈值 ${threshold}` } },
          markArea: { silent: true, itemStyle: { color: 'rgba(250,173,20,0.06)' }, data: [[{ yAxis: threshold }, { yAxis: 'max' }]] },
        }],
      };
    }
    case 'ring-progress': {
      const v = hasData ? Math.min(100, Math.max(0, Number(rows[0].value) || 0)) : 65;
      const color = String(config.progressColor || '#1890ff');
      const track = String(config.trackColor || 'rgba(255,255,255,0.08)');
      return {
        title: { text: `${v}%`, left: 'center', top: 'center', textStyle: { color: '#fff', fontSize: 26, fontWeight: 'bold' } },
        series: [{ type: 'pie', radius: ['62%', '78%'], silent: true, label: { show: false }, data: [{ value: v, itemStyle: { color } }, { value: Math.max(0, 100 - v), itemStyle: { color: track } }] }],
      };
    }
    case 'effect-scatter': {
      const pts = hasData ? rows.map((r) => r.value || [Number(r.x) || 0, Number(r.y) || 0]) : [[10, 20], [30, 50], [50, 30], [70, 60], [90, 40]];
      return {
        grid: { left: 50, right: 20, top: 20, bottom: 40 },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'effectScatter', data: pts, symbolSize: Number(config.symbolSize) || 10, rippleEffect: { scale: Number(config.rippleScale) || 3, brushType: 'stroke' }, itemStyle: { color: '#1890ff' } }],
      };
    }
    case 'scatter-cluster': {
      const threshold = Number(config.largeThreshold) || 2000;
      const pts = hasData ? rows.map((r) => [Number(r.x ?? r.value) || 0, Number(r.y) || 0]) : Array.from({ length: 300 }, (_, i) => [Math.round(Math.sin(i) * 100), Math.round(Math.cos(i * 1.7) * 100)]);
      return {
        grid: { left: 50, right: 20, top: 20, bottom: 40 },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{ type: 'scatter', large: true, largeThreshold: threshold, data: pts, symbolSize: 4, itemStyle: { color: 'rgba(24,144,255,0.65)' } }],
      };
    }
    case 'speedometer': {
      const v = hasData ? Number(rows[0]?.value) || 0 : 88;
      const unit = String(config.speedUnit || 'km/h');
      return {
        series: [{
          type: 'gauge', min: Number(config.gaugeMin ?? 0), max: Number(config.gaugeMax ?? 200),
          startAngle: Number(config.startAngle ?? 200), endAngle: Number(config.endAngle ?? -20),
          radius: '85%',
          axisLine: { lineStyle: { width: Number(config.axisWidth) || 14, color: [[0.3, '#52c41a'], [0.7, '#faad14'], [1, '#ff4d4f']] } },
          pointer: { itemStyle: { color: 'auto' }, width: 5 },
          axisTick: { distance: -14, length: 6, lineStyle: { color: '#fff', width: 1 } },
          splitLine: { distance: -14, length: 14, lineStyle: { color: '#fff', width: 2 } },
          axisLabel: { distance: 18, color: '#aaa', fontSize: 10 },
          detail: { valueAnimation: true, formatter: `{value} ${unit}`, color: '#fff', fontSize: 18, offsetCenter: [0, '65%'] },
          data: [{ value: v }],
        }],
      };
    }
    case 'boxplot': {
      const groups = hasData
        ? rows.map((r) => (Array.isArray(r.value) ? r.value.map(Number) : [Number(r.value) || 0]))
        : [[650, 850, 940, 980, 1170], [720, 890, 950, 1000, 1200], [620, 800, 900, 960, 1100]];
      return {
        tooltip: { trigger: 'item' },
        grid: { left: 50, right: 20, top: 20, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['A', 'B', 'C'], axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        series: [{
          type: 'boxplot', data: groups,
          itemStyle: { color: 'rgba(24,144,255,0.25)', borderColor: '#1890ff' },
          markPoint: { data: [{ type: 'max', name: '最大值' }], itemStyle: { color: config.outlierColor || '#ff4d4f' } },
        }],
      };
    }
    case 'parallel': {
      const dims = String(config.dimensions || 'A,B,C,D,E').split(',').map((s) => s.trim()).filter(Boolean);
      const opacity = Number(config.lineOpacity ?? 0.5);
      const data = hasData
        ? rows.map((r, i) => ({
          name: rowLabel(r) || `行${i + 1}`,
          value: dims.map((_, d) => Number(r[`v${d + 1}`] ?? (d === 0 ? r.value : 0)) || 0),
          lineStyle: { opacity },
        }))
        : [{ name: '示例', value: dims.map((_, i) => 50 + i * 10), lineStyle: { opacity } }];
      return {
        parallel: { left: 50, right: 50, top: 40, bottom: 30, parallelAxisDefault: { axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined }, splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLine: { lineStyle: { color: '#3a3a5e' } } } },
        parallelAxis: dims.map((d, i) => ({ dim: i, name: d })),
        series: data.map((d) => ({ type: 'parallel', ...d, smooth: true })),
      };
    }
    case 'theme-river': {
      const colors = splitList(config.riverColors, ['#1890ff', '#52c41a', '#faad14', '#ff4d4f']);
      const data: Array<[string, number, string]> = [];
      if (hasData) {
        rows.forEach((r) => data.push([String(r.date ?? rowLabel(r)), Number(r.value) || 0, String(r.name ?? r.series ?? '系列')]));
      }
      if (!data.length) {
        const names = ['甲', '乙', '丙'];
        for (let t = 0; t < 12; t += 1) {
          names.forEach((n, i) => data.push([`2026-${String(t + 1).padStart(2, '0')}-01`, Math.round(20 + 15 * Math.sin((t + i) / 2) + i * 5), n]));
        }
      }
      return {
        tooltip: { trigger: 'axis' },
        legend,
        singleAxis: { top: 50, bottom: 30, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined }, splitLine: { lineStyle: { color: '#2a2a3e' } } },
        series: [{ type: 'themeRiver', data, label: { color: '#ccc' }, itemStyle: { color: (p: any) => colors[(p?.dataIndex ?? 0) % colors.length] } }],
      };
    }
    case 'force-graph': {
      const nodes = Array.from(new Set(rows.flatMap((r) => [r.name ?? r.from, r.to]))).filter(Boolean).map((n, i) => ({
        name: n, symbolSize: 22, draggable: true, itemStyle: { color: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f'][i % 4] },
      }));
      const links = rows.filter((r) => r.to).map((r) => ({ source: r.name ?? r.from, target: r.to, value: Number(r.value) || 1 }));
      return {
        tooltip: { trigger: 'item' },
        legend,
        series: [{
          type: 'graph', layout: 'force', roam: true, draggable: true,
          force: { repulsion: Number(config.repulsion) || 120, edgeLength: Number(config.edgeLength) || 100 },
          data: nodes.length ? nodes : ['A', 'B', 'C', 'D'].map((n) => ({ name: n, symbolSize: 22 })),
          links,
          lineStyle: { color: 'source', opacity: 0.6, width: 1 }, label: { color: '#ccc' },
        }],
      };
    }
    case 'bar-line-mix': {
      const barKey = String(config.barKey || 'bar');
      const lineKey = String(config.lineKey || 'line');
      const barData = hasData ? rows.map((r) => Number(r[barKey]) || 0) : [120, 200, 150, 80, 70, 110];
      const lineData = hasData ? rows.map((r) => Number(r[lineKey]) || 0) : [60, 80, 70, 90, 60, 100];
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { left: 50, right: 50, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: hasData ? labels : ['一月', '二月', '三月', '四月', '五月', '六月'], axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined } },
        yAxis: [
          { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
          { type: 'value', splitLine: { show: false }, axisLabel: { color: '#aaa', fontFamily: config.axisFontFamily || undefined } },
        ],
        series: [
          { type: 'bar', name: barKey, data: barData, itemStyle: { color: config.barColor || '#1890ff', borderRadius: [4, 4, 0, 0] } },
          { type: 'line', name: lineKey, yAxisIndex: 1, smooth: true, data: lineData, symbol: 'circle', lineStyle: { width: 3, color: '#faad14' } },
        ],
      };
    }
    // ---- G-00C 批次C：3D / 地图（工厂可表达部分，__gl 按需加载 echarts-gl）----
    case 'scatter-3d': {
      const pts = hasData
        ? rows.map((r, i) => [Number(r.x) || i, Number(r.y) || Number(r.value) || 0, Number(r.z) || Number(r.value) || 0])
        : [[0, 0, 10], [1, 2, 20], [2, 1, 30], [3, 3, 25], [4, 2, 40]];
      return {
        __gl: true,
        tooltip: {},
        xAxis3D: { type: 'value' }, yAxis3D: { type: 'value' }, zAxis3D: { type: 'value' },
        grid3D: { boxWidth: 80, boxDepth: 80, viewControl: { autoRotate: false } },
        series: [{ type: 'scatter3D', symbolSize: Number(config.symbolSize) || 10, data: pts, itemStyle: { color: '#1890ff', opacity: Number(config.pointOpacity ?? 1) } }],
      };
    }
    case 'surface-3d': {
      const size = Math.max(2, Math.min(60, Number(config.gridSize) || 12));
      const data: number[][] = [];
      for (let i = 0; i < size; i += 1) {
        for (let j = 0; j < size; j += 1) {
          data.push([i, j, Math.sin(i / 2) * Math.cos(j / 2) * 10 + 10]);
        }
      }
      return {
        __gl: true,
        tooltip: {},
        xAxis3D: { type: 'value' }, yAxis3D: { type: 'value' }, zAxis3D: { type: 'value' },
        grid3D: { viewControl: { autoRotate: false } },
        series: [{ type: 'surface', data, dataShape: [size, size], shading: 'lambert', wireframe: { show: false }, itemStyle: { opacity: Number(config.surfaceOpacity ?? 0.9) } }],
      };
    }
    case 'globe-3d': {
      const pts = hasData && rows.some((r) => r.lng !== undefined)
        ? rows.map((r) => ({ name: r.name, value: [Number(r.lng) || 116.4, Number(r.lat) || 39.9, Number(r.value) || 10] }))
        : [{ name: '北京', value: [116.4, 39.9, 30] }, { name: '上海', value: [121.47, 31.23, 20] }, { name: '广州', value: [113.26, 23.13, 15] }];
      return {
        __gl: true,
        tooltip: {},
        globe: {
          shading: 'lambert',
          atmosphere: { show: true },
          light: { main: { intensity: 1.2 }, ambient: { intensity: 0.4 } },
          viewControl: { autoRotate: config.autoRotate !== false, autoRotateSpeed: 6 },
        },
        series: [{ type: 'scatter3D', coordinateSystem: 'globe', data: pts, symbolSize: 8, itemStyle: { color: config.baseColor || '#ffcc00' } }],
      };
    }
    case 'flyline-3d': {
      const geoUrl = '/assets/geo/china.json';
      const lines = rows.filter((r) => r.from && r.to).map((r) => ({
        coords: [[Number(r.fromLng) || 116.4, Number(r.fromLat) || 39.9], [Number(r.toLng) || 121.47, Number(r.toLat) || 31.23]],
        value: Number(r.value) || 1,
      }));
      const data = lines.length ? lines : [
        { coords: [[116.4, 39.9], [121.47, 31.23]], value: 10 },
        { coords: [[113.26, 23.13], [104.07, 30.67]], value: 6 },
      ];
      return {
        __gl: true,
        __geoUrl: geoUrl,
        tooltip: {},
        geo3D: { map: 'china', boxDepth: 80, regionHeight: 2, itemStyle: { color: '#12304f', borderColor: '#3a5a80' }, viewControl: { autoRotate: false }, light: { main: { intensity: 1.2 }, ambient: { intensity: 0.3 } } },
        series: [{
          type: 'lines3D', coordinateSystem: 'geo3D',
          effect: { show: true, trailWidth: Number(config.lineWidth3d) || 2, trailLength: Number(config.trailLength ?? 0.3), trailOpacity: 0.8, constantSpeed: 20 },
          blendMode: 'lighter',
          lineStyle: { width: 1, color: config.lineColor3d || '#ffcc00' },
          data,
        }],
      };
    }
    case 'map-heatmap': {
      const geoUrl = String(config.mapJsonUrl || '/assets/geo/china.json');
      const mapName = geoUrl.split('/').pop()!.replace(/\.json$/, '');
      const max = Number(config.visualMapMax) || 100;
      return {
        __geoUrl: geoUrl,
        tooltip: { trigger: 'item' },
        visualMap: { min: 0, max, calculable: true, inRange: { color: ['#12304f', '#1890ff', '#ff4d4f'] }, left: 10, bottom: 10, textStyle: { color: '#aaa' } },
        geo: { map: mapName, roam: true, itemStyle: { areaColor: '#0f2233', borderColor: '#3a5a80' }, emphasis: { itemStyle: { areaColor: '#1b3a55' } } },
        series: [{
          type: 'heatmap', coordinateSystem: 'geo',
          data: rows.map((r) => [Number(r.lng) || 116.4, Number(r.lat) || 39.9, Number(r.value) || 0]),
          pointSize: 6, blurSize: 10,
        }],
      };
    }
    case 'map-bubble': {
      const geoUrl = String(config.mapJsonUrl || '/assets/geo/china.json');
      const mapName = geoUrl.split('/').pop()!.replace(/\.json$/, '');
      const maxSize = Number(config.symbolSizeMax) || 30;
      return {
        __geoUrl: geoUrl,
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        geo: { map: mapName, roam: true, itemStyle: { areaColor: '#0f2233', borderColor: '#3a5a80' }, label: { show: true, color: '#9db2cc', fontSize: 9 } },
        series: [{
          type: 'scatter', coordinateSystem: 'geo',
          data: rows.map((r) => ({ name: r.name, value: [Number(r.lng) || 116.4, Number(r.lat) || 39.9, Number(r.value) || 10] })),
          symbolSize: (val: any) => Math.max(6, Math.min(maxSize, Number(val?.[2]) || 10)),
          itemStyle: { color: 'rgba(24,144,255,0.75)' },
        }],
      };
    }
    case 'map-3d': {
      const geoUrl = String(config.mapJsonUrl || '/assets/geo/china.json');
      const mapName = geoUrl.split('/').pop()!.replace(/\.json$/, '');
      return {
        __gl: true,
        __geoUrl: geoUrl,
        tooltip: {},
        geo3D: {
          map: mapName, boxDepth: Number(config.viewHeight) || 80, regionHeight: 2,
          itemStyle: { color: config.baseColor || '#12304f', borderColor: '#3a5a80' },
          viewControl: { autoRotate: false },
          light: { main: { intensity: 1.2 }, ambient: { intensity: 0.3 } },
        },
        series: [],
      };
    }
    // ---- 2026-10-13 用户组件包：docs/组件 17 个（ECharts 示例移植，统一走本工厂）----
    case 'percent-stack-bar': {
      // 百分比堆叠柱状图（bar-stack-normalization）：按列归一化 + 百分比标签
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3,v4,v5');
      const colors = splitList(config.stackColors, ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de']);
      const demoCats = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const demoRaw = [
        [100, 302, 301, 334, 390, 330, 320],
        [320, 132, 101, 134, 90, 230, 210],
        [220, 182, 191, 234, 290, 330, 310],
        [150, 212, 201, 154, 190, 330, 410],
        [820, 832, 901, 934, 1290, 1330, 1320],
      ];
      const cats = hasData ? labels : demoCats;
      const rawAt = (ci: number, ki: number) => (hasData ? Number(rows[ci]?.[keys[ki]]) || 0 : demoRaw[ki % 5][ci % 7]);
      const totals = cats.map((_, ci) => keys.reduce((s, __, ki) => s + rawAt(ci, ki), 0));
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 50, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: cats, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', max: 1, splitNumber: 5, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, formatter: (v: number) => `${Math.round(v * 1000) / 10}%` }, splitLine: { lineStyle: { color: '#2a2a3e' } } },
        series: keys.map((k, ki) => ({
          name: k,
          type: 'bar',
          stack: 'total',
          barWidth: config.barWidth || '60%',
          label: { show: config.showLabel !== false, color: '#e6ecf5', fontSize: 10, formatter: (p: any) => `${Math.round((p.value || 0) * 1000) / 10}%` },
          itemStyle: { color: colors[ki % colors.length] },
          data: cats.map((_, ci) => (totals[ci] <= 0 ? 0 : rawAt(ci, ki) / totals[ci])),
        })),
      };
    }
    case 'sort-bar': {
      // 排序柱状图（data-transform-sort-bar）：按数值排序 + 标签旋转
      const order = String(config.sortOrder || 'desc');
      const demo: Array<[string, number]> = [['Hannah Krause', 314], ['Zhao Qian', 351], ['Jasmin Krause', 287], ['Li Lei', 219], ['Karle Neumann', 253], ['Mia Neumann', 165], ['Böhm Fuchs', 318], ['Han Meimei', 366]];
      const pairs: Array<[string, number]> = hasData ? rows.map((r) => [rowLabel(r), rowValue(r)]) : demo;
      const sorted = pairs.slice().sort((a, b) => (order === 'asc' ? a[1] - b[1] : b[1] - a[1]));
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 50, right: 24, top: 30, bottom: 72 },
        xAxis: { type: 'category', data: sorted.map((p) => p[0]), axisLabel: { interval: 0, rotate: Number(config.axisLabelRotate ?? 30), color: '#aaa', fontSize: config.axisFontSize || 12 }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa' } },
        series: [{ type: 'bar', barWidth: config.barWidth || '50%', itemStyle: { color: config.barColor || '#5470c6', borderRadius: [4, 4, 0, 0] }, label: { show: config.showLabel !== false, position: 'top', color: '#ddd', fontSize: 10 }, data: sorted.map((p) => p[1]) }],
      };
    }
    case 'group-bar': {
      // 多系列柱状图（dataset-simple0）：并列分组展示
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3');
      const colors = splitList(config.seriesColors, ['#5470c6', '#91cc75', '#fac858']);
      const demo = [['Matcha Latte', 43.3, 85.8, 93.7], ['Milk Tea', 83.1, 73.4, 55.1], ['Cheese Cocoa', 86.4, 65.2, 82.5], ['Walnut Brownie', 72.4, 53.9, 39.1]];
      const cats = hasData ? labels : demo.map((d) => String(d[0]));
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend,
        grid: { left: 46, right: 20, top: legend ? 40 : 24, bottom: 40 },
        xAxis: { type: 'category', data: cats, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12, fontFamily: config.axisFontFamily || undefined }, axisLine: { lineStyle: { color: '#333' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#aaa' } },
        series: keys.map((k, ki) => ({
          name: k,
          type: 'bar',
          barGap: config.barGap || '10%',
          barWidth: config.barWidth || undefined,
          itemStyle: { color: colors[ki % colors.length], borderRadius: [3, 3, 0, 0] },
          data: hasData ? rows.map((r) => Number(r[k]) || 0) : demo.map((d) => Number(d[ki + 1]) || 0),
        })),
      };
    }
    case 'pictorial-forest': {
      // 象形树柱图（pictorialBar-forest）：以树木符号重复绘制的镜像柱
      const symbol = String(config.symbolUrl || '') || TREE_SYMBOL_PATH;
      const size = Number(config.symbolSize) || 30;
      const demo = [5, 12, 22, 34, 46, 55, 46, 34, 22, 12];
      const vals = hasData ? values : demo;
      const cats = vals.map((_, i) => String(i));
      const maxAbs = Math.max(1, ...vals.map((v) => Math.abs(Number(v) || 0)));
      return {
        tooltip: { trigger: 'axis' },
        grid: { top: 'center', height: '72%', left: 40, right: 40 },
        xAxis: {
          type: 'value', show: false, min: -maxAbs * 1.15, max: maxAbs * 1.15,
          name: String(config.centerYear || '2026'), nameLocation: 'middle', nameGap: 46,
          nameTextStyle: { color: config.yearColor || '#52c41a', fontSize: Number(config.yearFontSize) || 26, fontFamily: 'Arial' },
        },
        yAxis: { type: 'category', data: cats, show: false },
        series: [
          { name: 'up', type: 'pictorialBar', symbol, symbolSize: [size, size * 1.8], symbolRepeat: true, animationEasing: 'elasticOut', itemStyle: { color: config.barColor || '#2f9e44' }, data: vals.map((v, i) => ({ value: Number(v) || 0, symbolOffset: i % 2 ? ['50%', 0] : undefined })) },
          { name: 'down', type: 'pictorialBar', symbol, symbolSize: [size, size * 1.8], symbolRepeat: true, animationEasing: 'elasticOut', itemStyle: { color: config.barColor2 || '#1d7a33' }, data: vals.map((v, i) => ({ value: -(Number(v) || 0), symbolOffset: i % 2 ? undefined : ['50%', 0] })) },
        ],
      };
    }
    case 'touch-zoom-line': {
      // 可缩放折线图（line-tooltip-touch）：双系列渐变面积 + 内置 dataZoom
      const c1 = String(config.colorFrom || '#3a4de9');
      const c2 = String(config.colorTo || '#f2597f');
      const demoLabels = ['10-04', '10-05', '10-06', '10-07', '10-08', '10-09', '10-10', '10-11', '10-12'];
      const s1 = hasData ? values : [180, 165, 172, 155, 149, 160, 152, 148, 143];
      const s2 = hasData ? rows.map((r) => Number(r.v2) || 0) : [28, 33, 27, 31, 36, 30, 34, 29, 32];
      const zoomStart = Number(config.zoomStart ?? 0);
      const zoomEnd = Number(config.zoomEnd ?? 100);
      return {
        tooltip: { trigger: 'axis' },
        legend,
        grid: { top: legend ? 54 : 28, left: 15, right: 15, bottom: 30, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: hasData ? labels : demoLabels, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12 }, axisLine: { lineStyle: { color: '#333' } }, splitLine: { show: false } },
        yAxis: { type: 'value', splitLine: { show: false }, axisLabel: { color: '#aaa', inside: true, formatter: '{value}\n' } },
        dataZoom: [{ type: 'inside', throttle: 50, start: zoomStart, end: zoomEnd }],
        series: [
          { name: String(config.series1Name || 'Intention'), type: 'line', stack: 'a', smooth: true, symbol: 'circle', symbolSize: 5, sampling: 'average', itemStyle: { color: c1 }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: c1 }, { offset: 1, color: 'rgba(58,77,233,0.1)' }]) }, data: s1 },
          { name: String(config.series2Name || 'Second'), type: 'line', stack: 'a', smooth: true, symbol: 'circle', symbolSize: 5, sampling: 'average', itemStyle: { color: c2 }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: c2 }, { offset: 1, color: 'rgba(213,72,120,0.1)' }]) }, data: s2 },
        ],
      };
    }
    case 'rainfall-flow-area': {
      // 双轴面积图（area-rainfall）：流量 + 倒置雨量双 Y 轴 + dataZoom
      const demoLabels = Array.from({ length: 30 }, (_, i) => `${i + 1}日`);
      const flow = hasData ? values : [0.97, 0.96, 0.95, 0.94, 0.94, 0.93, 0.94, 0.95, 0.98, 1.05, 1.2, 1.4, 1.5, 1.45, 1.3, 1.15, 1.05, 1.0, 0.97, 0.95, 0.94, 0.94, 0.95, 0.97, 1.0, 1.05, 1.1, 1.08, 1.02, 0.98];
      const rain = hasData ? rows.map((r) => Number(r.v2) || 0) : [0, 0, 0, 0, 0.02, 0.05, 0.03, 0, 0, 0, 0, 0.1, 0.2, 0.15, 0.05, 0, 0, 0, 0.08, 0.3, 0.6, 0.2, 0.05, 0, 0, 0, 0, 0.04, 0.02, 0];
      const zoomStart = Number(config.zoomStart ?? 0);
      const zoomEnd = Number(config.zoomEnd ?? 100);
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross', animation: false, label: { backgroundColor: '#505765' } } },
        legend,
        grid: { bottom: 72, left: 64, right: 56, top: legend ? 40 : 24 },
        dataZoom: [
          { show: true, realtime: true, start: zoomStart, end: zoomEnd },
          { type: 'inside', realtime: true, start: zoomStart, end: zoomEnd },
        ],
        xAxis: [{ type: 'category', boundaryGap: false, axisLine: { lineStyle: { color: '#333' } }, axisLabel: { color: '#aaa', fontSize: config.axisFontSize || 12 }, data: hasData ? labels : demoLabels }],
        yAxis: [
          { name: String(config.y1Name || 'Flow(m³/s)'), type: 'value', splitLine: { lineStyle: { color: '#2a2a3e' } }, axisLabel: { color: '#9db2cc' }, nameTextStyle: { color: '#9db2cc' } },
          { name: String(config.y2Name || 'Rainfall(mm)'), nameLocation: 'start', alignTicks: true, inverse: config.inverse2 !== false, type: 'value', splitLine: { show: false }, axisLabel: { color: '#fac858' }, nameTextStyle: { color: '#fac858' } },
        ],
        series: [
          { name: String(config.y1Name || 'Flow(m³/s)'), type: 'line', areaStyle: { opacity: 0.45 }, lineStyle: { width: 1 }, emphasis: { focus: 'series' }, itemStyle: { color: '#5470c6' }, data: flow },
          { name: String(config.y2Name || 'Rainfall(mm)'), type: 'line', yAxisIndex: 1, areaStyle: { opacity: 0.45 }, lineStyle: { width: 1 }, emphasis: { focus: 'series' }, itemStyle: { color: '#fac858' }, data: rain },
        ],
      };
    }
    case 'radar-dual': {
      // 双序列雷达图（radar）：多指标对比
      const indicators = parseJson<Array<{ name: string; max: number }>>(config.indicatorJson, [
        { name: 'Sales', max: 6500 }, { name: 'Administration', max: 16000 }, { name: 'IT', max: 30000 },
        { name: 'Support', max: 38000 }, { name: 'Development', max: 52000 }, { name: 'Marketing', max: 25000 },
      ]);
      const keys = splitKeys(config.seriesKeys, 'v1,v2');
      const colors = splitList(config.seriesColors, ['#5470c6', '#91cc75']);
      const demo = [[4200, 3000, 20000, 35000, 50000, 18000], [5000, 14000, 28000, 26000, 42000, 21000]];
      return {
        tooltip: { trigger: 'item' },
        legend,
        radar: {
          indicator: indicators.length ? indicators : [{ name: 'A', max: 100 }],
          shape: config.radarShape === 'circle' ? 'circle' : 'polygon',
          splitArea: { show: true, areaStyle: { color: ['rgba(255,255,255,0.02)', 'rgba(255,255,255,0.06)'] } },
          splitLine: { lineStyle: { color: '#2a2a3e' } },
          axisLine: { lineStyle: { color: '#2a2a3e' } },
          axisName: { color: '#9db2cc' },
        },
        series: [{
          type: 'radar',
          data: keys.map((k, ki) => ({
            name: k,
            value: hasData ? indicators.map((_, ii) => Number(rows[ii]?.[k]) || 0) : demo[ki % demo.length],
            itemStyle: { color: colors[ki % colors.length] },
            lineStyle: { width: Number(config.lineWidth) || 2 },
            areaStyle: { opacity: Number(config.areaOpacity ?? 0.15) },
          })),
        }],
      };
    }
    case 'radar-aqi': {
      // AQI 雷达图（radar-aqi）：深色主题 + 金色网格 + 圆环雷达
      const indicators = parseJson<Array<{ name: string; max: number }>>(config.indicatorJson, [
        { name: 'AQI', max: 300 }, { name: 'PM2.5', max: 250 }, { name: 'PM10', max: 300 },
        { name: 'CO', max: 5 }, { name: 'NO2', max: 200 }, { name: 'SO2', max: 100 },
      ]);
      const keys = splitKeys(config.seriesKeys, 'v1,v2,v3');
      const colors = splitList(config.seriesColors, ['#F9713C', '#B3E4A1', 'rgb(238,197,102)']);
      const demo = [[55, 38, 52, 0.66, 48, 25], [39, 24, 39, 0.59, 50, 19], [188, 143, 197, 1.66, 99, 51]];
      return {
        backgroundColor: config.bgDark === false ? undefined : String(config.chartBg || '#161627'),
        tooltip: { trigger: 'item' },
        legend: legend ? { ...legend, top: undefined, bottom: 5, selectedMode: 'single', textStyle: { color: '#fff', fontSize: 12 } } : undefined,
        radar: {
          indicator: indicators.length ? indicators : [{ name: 'A', max: 100 }],
          shape: 'circle',
          splitNumber: Number(config.splitNumber) || 5,
          axisName: { color: 'rgb(238,197,102)' },
          splitLine: { lineStyle: { color: ['rgba(238,197,102,0.1)', 'rgba(238,197,102,0.2)', 'rgba(238,197,102,0.4)', 'rgba(238,197,102,0.6)', 'rgba(238,197,102,0.8)', 'rgba(238,197,102,1)'].reverse() } },
          splitArea: { show: false },
          axisLine: { lineStyle: { color: 'rgba(238,197,102,0.5)' } },
        },
        series: keys.map((k, ki) => ({
          name: k,
          type: 'radar',
          symbol: 'none',
          itemStyle: { color: colors[ki % colors.length] },
          lineStyle: { width: 1, opacity: 0.5 },
          areaStyle: { opacity: Number(config.areaOpacity ?? 0.08) },
          data: hasData ? [{ value: indicators.map((_, ii) => Number(rows[ii]?.[k]) || 0) }] : [{ value: demo[ki % demo.length] }],
        })),
      };
    }
    case 'gauge-simple': {
      // 简约仪表盘（gauge-simple）：横向进度 + 大数值
      const value = hasData ? Number(rows[0]?.value) || 0 : 50;
      return {
        tooltip: { formatter: '{a} <br/>{b} : {c}%' },
        series: [{
          name: String(config.gaugeName || 'Pressure'),
          type: 'gauge',
          min: Number(config.gaugeMin ?? 0),
          max: Number(config.gaugeMax ?? 100),
          progress: { show: config.showProgress !== false },
          axisLine: { lineStyle: { color: [[1, 'rgba(255,255,255,0.12)']], width: Number(config.axisWidth) || 12 } },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#3a4a5f' } },
          axisLabel: { color: '#9db2cc', fontSize: 10 },
          detail: { valueAnimation: true, formatter: `{value}${config.detailSuffix ?? '%'}`, color: '#fff', fontSize: Number(config.detailFontSize) || 30, offsetCenter: [0, '62%'] },
          data: [{ value, name: String(config.dataName || 'SCORE') }],
        }],
      };
    }
    case 'gauge-thick': {
      // 粗环仪表盘（gauge-progress）：宽进度环 + 指针锚点 + 底部大数值
      const value = hasData ? Number(rows[0]?.value) || 0 : 70;
      const width = Number(config.axisWidth) || 18;
      return {
        series: [{
          type: 'gauge',
          min: Number(config.gaugeMin ?? 0),
          max: Number(config.gaugeMax ?? 100),
          progress: { show: true, width },
          axisLine: { lineStyle: { width } },
          axisTick: { show: false },
          splitLine: { length: 15, lineStyle: { width: 2, color: '#999' } },
          axisLabel: { distance: 25, color: '#999', fontSize: 20 },
          anchor: { show: true, showAbove: true, size: 25, itemStyle: { borderWidth: 10, color: config.pointerColor || '#5470c6' } },
          title: { show: false },
          detail: { valueAnimation: true, fontSize: Number(config.detailFontSize) || 48, offsetCenter: [0, '70%'], color: '#fff', formatter: `{value}${config.detailSuffix ?? ''}` },
          data: [{ value }],
        }],
      };
    }
    case 'custom-polar-gauge': {
      // 极坐标仪表盘（custom-gauge）：custom series 绘制扇形进度 + 指针 + 中心百分比
      const max = Number(config.gaugeMax) || 200;
      const value = hasData ? Number(rows[0]?.value) || 0 : 156;
      const color = String(config.color || '#5470c6');
      const OUTER = 200, INNER = 170, POINTER_INNER = 40, PANEL_R = 140;
      const makeText = (v: number) => `${((v / max) * 100).toFixed(0)}%`;
      const polarPoint = (p: any, radius: number, radian: number) => [Math.cos(radian) * radius + p.coordSys.cx, -Math.sin(radian) * radius + p.coordSys.cy];
      const pointerPoints = (p: any, radian: number) => [
        polarPoint(p, OUTER, radian),
        polarPoint(p, POINTER_INNER, radian + Math.PI * 0.03),
        polarPoint(p, POINTER_INNER, radian),
        polarPoint(p, POINTER_INNER, radian - Math.PI * 0.03),
      ];
      return {
        animationEasingUpdate: 'quarticInOut',
        dataset: { source: [[1, value]] },
        tooltip: {},
        angleAxis: { type: 'value', startAngle: 0, show: false, min: 0, max },
        radiusAxis: { type: 'value', show: false },
        polar: {},
        series: [{
          type: 'custom',
          coordinateSystem: 'polar',
          renderItem: (params: any, api: any) => {
            const valOnRadian = api.value(1);
            const coords = api.coord([api.value(0), valOnRadian]);
            const polarEndRadian = coords[3];
            return {
              type: 'group',
              children: [
                {
                  type: 'sector',
                  shape: { cx: params.coordSys.cx, cy: params.coordSys.cy, r: OUTER, r0: INNER, startAngle: 0, endAngle: -polarEndRadian, transition: 'endAngle', enterFrom: { endAngle: 0 } },
                  style: { fill: color },
                },
                { type: 'polygon', shape: { points: pointerPoints(params, polarEndRadian) }, style: { fill: color, shadowBlur: 12, shadowColor: 'rgba(0,0,0,0.35)' } },
                { type: 'circle', shape: { cx: params.coordSys.cx, cy: params.coordSys.cy, r: PANEL_R }, style: { fill: 'rgba(12,22,40,0.88)', shadowBlur: 25, shadowColor: 'rgba(76,107,167,0.4)' } },
                {
                  type: 'text',
                  extra: { valOnRadian, transition: 'valOnRadian', enterFrom: { valOnRadian: 0 } },
                  style: { text: makeText(valOnRadian), fontSize: Number(config.detailFontSize) || 40, fontWeight: 700, x: params.coordSys.cx, y: params.coordSys.cy, fill: '#fff', align: 'center', verticalAlign: 'middle' },
                  during: (apiDuring: any) => apiDuring.setStyle('text', makeText(apiDuring.getExtra('valOnRadian'))),
                },
              ],
            };
          },
        }],
      };
    }
    case 'calendar-multi': {
      // 多日历组合图（calendar-charts）：多个月份日历 + 热力/涟漪散点
      const monthsCfg = String(config.months || '').split(',').map((s) => s.trim()).filter(Boolean);
      const months = monthsCfg.length >= 2 ? monthsCfg : ['2026-10', '2026-09'];
      const cell = Number(config.cellSize) || 40;
      const genMonth = (m: string) => Array.from({ length: 30 }, (_, i) => {
        const date = `${m}-${String(i + 1).padStart(2, '0')}`;
        const value = Math.round(Math.abs(Math.sin((i + 1) * 7.13)) * 1000);
        return [date, value] as [string, number];
      });
      const data0 = hasData ? rows.map((r) => [String(r.date ?? rowLabel(r)), Number(r.value) || 0] as [string, number]) : genMonth(months[0]);
      const data1 = hasData ? data0 : genMonth(months[1]);
      const maxV = Math.max(1, ...data0.map((d) => d[1]));
      return {
        tooltip: { position: 'top' },
        visualMap: { min: 0, max: Number(config.visualMapMax) || maxV, calculable: true, seriesIndex: [0], orient: 'horizontal', left: 'center', bottom: 10, inRange: { color: ['#12304f', '#1890ff', '#ff4d4f'] }, textStyle: { color: '#aaa' } },
        calendar: [
          { orient: 'vertical', yearLabel: { margin: 40, color: '#aaa' }, monthLabel: { nameMap: 'cn', margin: 20, color: '#9db2cc' }, dayLabel: { firstDay: 1, nameMap: 'cn', color: '#9db2cc' }, cellSize: cell, range: months[0], left: 60, top: 60, itemStyle: { color: 'transparent', borderColor: '#2a3a4e' } },
          { orient: 'vertical', yearLabel: { margin: 40, color: '#aaa' }, monthLabel: { nameMap: 'cn', margin: 20, color: '#9db2cc' }, dayLabel: { firstDay: 1, nameMap: 'cn', color: '#9db2cc' }, cellSize: cell, range: months[1], top: 60, left: '50%', itemStyle: { color: 'transparent', borderColor: '#2a3a4e' } },
        ],
        series: [
          { type: 'heatmap', coordinateSystem: 'calendar', calendarIndex: 0, data: data0 },
          { type: 'effectScatter', coordinateSystem: 'calendar', calendarIndex: 1, symbolSize: (val: any) => Math.max(2, (Number(val?.[1]) || 0) / 60), itemStyle: { color: '#fac858' }, data: data1 },
        ],
      };
    }
    case 'calendar-lunar': {
      // 农历日历图（calendar-lunar）：日历 + 日期/农历双行标签 + 热力
      const range = String(config.range || '2026-10');
      const cell = Number(config.cellSize) || 70;
      const dayLunar = ['初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十', '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十', '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十'];
      const days = Array.from({ length: 30 }, (_, i) => {
        const date = `${range}-${String(i + 1).padStart(2, '0')}`;
        const value = Math.round(Math.abs(Math.sin((i + 1) * 3.7)) * 300);
        return [date, value, dayLunar[i % 30]] as [string, number, string];
      });
      const data = hasData
        ? rows.map((r, i) => [String(r.date ?? rowLabel(r)), Number(r.value) || 0, String(r.lunar ?? dayLunar[i % 30])] as [string, number, string])
        : days;
      const labelColor = String(config.lunarColor || '#ff7875');
      return {
        tooltip: { formatter: (params: any) => `${params.value?.[0]}\n降雨量: ${params.value?.[1]}` },
        visualMap: { show: false, min: 0, max: Number(config.visualMapMax) || 300, seriesIndex: [2], inRange: { color: ['#e0ffff', '#006edd'], opacity: 0.35 } },
        calendar: {
          left: 'center', top: 'middle', cellSize: [cell, cell], yearLabel: { show: false }, orient: 'vertical',
          dayLabel: { firstDay: 1, nameMap: 'cn', color: '#9db2cc' }, monthLabel: { show: false, nameMap: 'cn' },
          range, itemStyle: { color: 'transparent', borderColor: '#2a3a4e' },
        },
        series: [
          {
            type: 'scatter', coordinateSystem: 'calendar', symbolSize: 0, silent: true,
            label: { show: true, color: '#e6ecf5', fontSize: 18, formatter: (p: any) => `${new Date(String(p.value?.[0])).getDate()}` },
            data,
          },
          {
            type: 'scatter', coordinateSystem: 'calendar', symbolSize: 0, silent: true,
            label: { show: config.showLunar !== false, color: labelColor, fontSize: 13, fontWeight: 700, formatter: (p: any) => String(p.value?.[2] || '') },
            data,
          },
          { name: '数值', type: 'heatmap', coordinateSystem: 'calendar', data: data.map((d) => [d[0], d[1]]) },
        ],
      };
    }
    case 'globe-atmosphere': {
      // 地球大气层（globe-atmosphere）：贴图地球 + 大气层光晕 + 星空环境
      return {
        __gl: true,
        backgroundColor: String(config.chartBg || '#000'),
        globe: {
          baseTexture: String(config.baseTexture || '/assets/globe/earth.jpg'),
          shading: 'lambert',
          environment: String(config.environment || '/assets/globe/starfield.jpg'),
          atmosphere: { show: config.atmosphere !== false },
          light: { ambient: { intensity: Number(config.ambientIntensity ?? 0.1) }, main: { intensity: Number(config.mainIntensity ?? 1.5) } },
          viewControl: { autoRotate: config.autoRotate !== false, autoRotateSpeed: Number(config.autoRotateSpeed) || 6 },
        },
        series: [],
      };
    }
    case 'globe-layers': {
      // 分层地球（globe-layers）：地球 + 夜景自发光层 + 云层叠加
      const layers: any[] = [];
      if (config.nightLayer !== false) layers.push({ type: 'blend', blendTo: 'emission', texture: String(config.nightTexture || '/assets/globe/night.jpg') });
      if (config.cloudLayer !== false) layers.push({ type: 'overlay', texture: String(config.cloudTexture || '/assets/globe/clouds.png'), shading: 'lambert', distance: 5 });
      return {
        __gl: true,
        backgroundColor: String(config.chartBg || '#000'),
        globe: {
          baseTexture: String(config.baseTexture || '/assets/globe/earth.jpg'),
          shading: 'lambert',
          environment: String(config.environment || '/assets/globe/starfield.jpg'),
          light: { ambient: { intensity: Number(config.ambientIntensity ?? 0.1) }, main: { intensity: Number(config.mainIntensity ?? 1.5) } },
          layers,
          viewControl: { autoRotate: config.autoRotate !== false, autoRotateSpeed: Number(config.autoRotateSpeed) || 6 },
        },
        series: [],
      };
    }
    case 'gps-scatter-gl': {
      // 海量 GPS 散点(gl)（scatterGL-gps）：GL 加速万级点位 + 地图底图
      const geoUrl = String(config.mapJsonUrl || '/assets/geo/china.json');
      const mapName = geoUrl.split('/').pop()!.replace(/\.json$/, '');
      const size = Number(config.pointSize) || 1.5;
      let pts: number[][];
      if (hasData && rows.some((r) => r.lng !== undefined || Array.isArray(r.value))) {
        pts = rows
          .map((r) => (Array.isArray(r.value) ? [Number(r.value[0]) || 0, Number(r.value[1]) || 0] : [Number(r.lng) || 0, Number(r.lat) || 0]))
          .filter((p) => p[0] !== 0 || p[1] !== 0);
      } else {
        // 确定性伪随机演示点（LCG；范围覆盖中国经纬度）
        let seed = 20261013;
        const rnd = () => { seed = (seed * 1103515245 + 12345) % 2147483648; return seed / 2147483648; };
        const count = Math.max(100, Math.min(20000, Number(config.demoCount) || 3000));
        pts = Array.from({ length: count }, () => [73 + rnd() * 62, 18 + rnd() * 35]);
      }
      return {
        __gl: true,
        __geoUrl: geoUrl,
        backgroundColor: String(config.chartBg || '#000'),
        tooltip: { show: false },
        geo: { map: mapName, roam: config.roam !== false, silent: true, itemStyle: { areaColor: '#323c48', borderColor: '#111' }, emphasis: { itemStyle: { areaColor: '#2a333d' } } },
        series: [{ type: 'scatterGL', coordinateSystem: 'geo', progressive: 1e6, symbolSize: size, blendMode: 'lighter', large: true, postEffect: { enable: true }, silent: true, itemStyle: { color: config.pointColor || 'rgb(150,180,255)' }, data: pts }],
      };
    }
    case 'pie-rich-label': {
      // 富文本标签饼图（pie-rich-text）：主扇区富文本明细卡（标题/表头/明细行）
      const demo = [{ name: 'CityE', value: 1548 }, { name: 'CityC', value: 735 }, { name: 'CityD', value: 510 }, { name: 'CityB', value: 434 }, { name: 'CityA', value: 335 }];
      const crows = hasData ? rows.map((r) => ({ name: rowLabel(r), value: rowValue(r) })) : demo;
      const total = crows.reduce((s, r) => s + (Number(r.value) || 0), 0) || 1;
      const rate = (v: number) => `${((Number(v) / total) * 100).toFixed(1)}%`;
      const lines = crows.slice(0, 3).map((r) => `  {dot|●} {name|${r.name}}{value|${r.value}}{rate|${rate(Number(r.value))}}`);
      const richLabel = ['{title|占比明细}', '  {nameHead|名称}{valueHead|数值}{rateHead|占比}', '{hr|}', ...lines].join('\n');
      const rich = {
        title: { color: '#eee', align: 'center', fontSize: 12, height: 22, backgroundColor: '#1890ff', width: '100%' },
        nameHead: { color: '#9db2cc', fontSize: 11 },
        valueHead: { color: '#9db2cc', fontSize: 11, align: 'center', width: 48 },
        rateHead: { color: '#9db2cc', fontSize: 11, align: 'right', width: 48 },
        dot: { color: '#1890ff', fontSize: 10 },
        name: { color: '#ddd', fontSize: 11 },
        value: { color: '#fff', fontSize: 11, align: 'center', width: 48 },
        rate: { color: '#fac858', fontSize: 11, align: 'right', width: 48 },
        hr: { borderColor: '#2a3a4e', width: '100%', borderWidth: 0.5, height: 0 },
      };
      return {
        tooltip: { trigger: 'item', formatter: '{a} <br/>{b} : {c} ({d}%)' },
        legend,
        series: [{
          name: '分布',
          type: 'pie',
          radius: config.isRing === false ? '65%' : ['38%', '65%'],
          center: ['50%', '50%'],
          selectedMode: 'single',
          itemStyle: { borderColor: '#0f0f2e', borderWidth: 2 },
          label: { show: config.showLabel !== false, color: '#ccc', formatter: '{b}: {d}%' },
          emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.5)' } },
          data: crows.map((r, i) => (i === 0
            ? {
              ...r,
              label: {
                show: true,
                formatter: richLabel,
                backgroundColor: String(config.labelBg || 'rgba(15,34,51,0.92)'),
                borderColor: '#3a5a80',
                borderWidth: 1,
                borderRadius: 4,
                padding: 8,
                lineHeight: 18,
                rich,
              },
            }
            : r)),
        }],
      };
    }
    // ---- 后续批次 ----
    default:
      return null;
  }
}

/** 解析 JSON 配置项（失败回退默认值） */
function parseJson<T>(v: unknown, fallback: T): T {
  if (typeof v !== 'string' || !v.trim()) return fallback;
  try {
    const parsed = JSON.parse(v) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/** 简单线性回归：返回拟合线两端点（供回归散点图） */
function linearFit(pts: [number, number][]): [number, number][] | null {
  const n = pts.length;
  if (n < 2) return null;
  let sx = 0, sy = 0, sxy = 0, sxx = 0;
  for (const [x, y] of pts) { sx += x; sy += y; sxy += x * y; sxx += x * x; }
  const denom = n * sxx - sx * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  const xs = pts.map((p) => p[0]);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  return [[x0, slope * x0 + intercept], [x1, slope * x1 + intercept]];
}

/** 逗号分隔 key 列表（带默认值） */
function splitKeys(v: unknown, fallback: string): string[] {
  const raw = String(v || '');
  const list = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return list.length ? list : fallback.split(',');
}

/** 逗号分隔色值列表（带默认值） */
function splitList(v: unknown, fallback: string[]): string[] {
  const raw = String(v || '');
  const list = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return list.length ? list : fallback;
}

// ============================================
// 地图支持（A-05）：本地 geo JSON 注册 + 迁徙坐标解析
// ============================================
const geoMapCache = new Map<string, Promise<any>>();

/** 加载并注册地图（url 本地 assets；name 缺省由 url 文件名推导） */
export async function ensureGeoMap(url: string, name?: string): Promise<string> {
  const mapName = name || url.split('/').pop()!.replace(/\.json$/, '');
  if (!geoMapCache.has(mapName)) {
    geoMapCache.set(
      mapName,
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`geo 加载失败 HTTP ${r.status}`);
          return r.json();
        })
        .then((geo) => {
          echarts.registerMap(mapName, geo);
          return geo;
        }),
    );
  }
  return geoMapCache.get(mapName)!;
}

/** 地图类 option 前置处理：注册地图 + 迁徙坐标填充（GenericChart setOption 前调用）
 *  同时按需加载 echarts 扩展（liquidfill/wordcloud，失败静默降级） */
export async function resolveGeoOption(option: any): Promise<void> {
  if (!option) return;
  // A-06 扩展插件按需加载（side-effect 注册）
  const ext = option.__ext as string[] | undefined;
  if (Array.isArray(ext)) {
    for (const e of ext) {
      try {
        if (e === 'liquidfill') await import('echarts-liquidfill');
        if (e === 'wordcloud') await import('echarts-wordcloud');
      } catch {
        /* 扩展加载失败：setOption 兜底，不影响其他组件 */
      }
    }
  }
  if (!option.__geoUrl) return;
  const geo = await ensureGeoMap(option.__geoUrl, option.__geoCoord ? 'china-map' : undefined).catch(() => null);
  if (option.__geoCoord && geo) {
    const coordMap = buildCoordMap(geo);
    option.series?.forEach((s: any) => {
      (s.data || []).forEach((d: any) => {
        const from = coordMap[d?._fromName];
        const to = coordMap[d?._toName];
        if (from) d.coords[0] = from;
        if (to) d.coords[1] = to;
      });
    });
    const pts: [number, number][] = [];
    for (const s of option.series || []) {
      for (const d of s.data || []) {
        if (d?.coords?.[0]) pts.push(d.coords[0]);
      }
    }
    const scatter = option.series?.find((s: any) => s.type === 'scatter');
    if (scatter) scatter.data = pts;
  }
}

/** 由 geo feature 名称 → 质心坐标 */
function buildCoordMap(geo: any): Record<string, [number, number]> {
  const out: Record<string, [number, number]> = {};
  const feats: any[] = geo?.features || [];
  for (const f of feats) {
    const name = String(f?.properties?.name || '');
    if (!name) continue;
    const b = f?.properties?.center || boundsCenter(f?.geometry);
    if (b) out[name] = [Number(b[0]), Number(b[1])];
  }
  return out;
}

function boundsCenter(geom: any): [number, number] | null {
  if (!geom) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (coords: any[]) => {
    for (const c of coords) {
      if (typeof c[0] === 'number') {
        minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]);
        minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]);
      } else walk(c);
    }
  };
  walk(geom.coordinates || []);
  if (minX === Infinity) return null;
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}